import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "../AppLayout";
import { EmbeddedLayoutContext } from "./EmbeddedLayout";
import { resolveSectionTab, sectionTabHref } from "./sectionTabs";

const NEON = "#FF10F0";

// One admin section (AI Studio, Money, Email, Social, Settings): a single page
// with a pink-neon tab bar. Each tab has its own ?tab=<id> URL, so tabs survive
// refresh, deep links and back/forward. Tabs with `href` are plain links to a
// related full page (for pages that own their own ?tab= state, e.g. X Composer).
export default function SectionShell({ sectionId, title, overline, tabs, aliases, actions }) {
  const [searchParams] = useSearchParams();
  const activeId = resolveSectionTab(tabs, searchParams.get("tab"), aliases);
  const active = tabs.find((t) => t.id === activeId);

  return (
    <AppLayout title={title} overline={overline} actions={actions}>
      <nav
        aria-label={`${title} sections`}
        data-testid={`${sectionId}-tabs`}
        className="mb-6 flex gap-2 overflow-x-auto border-b pb-3"
        style={{ borderColor: "rgba(255, 16, 240, 0.25)" }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          const cls = `flex shrink-0 items-center gap-2 rounded-t-lg px-3 py-2 text-sm font-medium transition ${
            isActive ? "text-white" : "text-zinc-400 hover:text-white hover:bg-[#FF10F0]/10"
          }`;
          const style = isActive
            ? { borderBottom: `2px solid ${NEON}`, background: "rgba(255, 16, 240, 0.12)", boxShadow: `0 6px 18px -8px ${NEON}` }
            : undefined;
          const body = (<>{tab.icon && <span aria-hidden="true">{tab.icon}</span>}{tab.label}{tab.href && <span aria-hidden="true">↗</span>}</>);
          return (
            <Link
              key={tab.id}
              to={tab.href || sectionTabHref(searchParams, tab.id)}
              data-testid={`${sectionId}-tab-${tab.id}`}
              aria-current={isActive ? "page" : undefined}
              className={cls}
              style={style}
            >
              {body}
            </Link>
          );
        })}
      </nav>
      {active?.description && <p className="-mt-3 mb-5 text-sm text-zinc-400" data-testid={`${sectionId}-tab-description`}>{active.description}</p>}
      <div data-testid={`${sectionId}-panel-${activeId}`}>
        <EmbeddedLayoutContext.Provider value={true}>
          {active ? active.render() : null}
        </EmbeddedLayoutContext.Provider>
      </div>
    </AppLayout>
  );
}
